import React, { useState } from 'react'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import { t } from '../../../i18n/i18n'
import { List } from '../../common/list/List'
import { Activity } from '../../../api/schemas/schema'
import { DashboardInfoCard } from '../DashboardInfoCard'
import { getDashboardActiviyColumns, getSortedLatestActivities } from '../../../helpers/dashboardHelper'
import { makeStyles, MessageBar, MessageBarBody, MessageBarTitle, Text, tokens } from '@fluentui/react-components'
import { ActivityStatusBadge } from '../ActivityStatusBadge'
import { compareByDate, formatDate } from '../../../helpers/stringHelper'
import { ActivityPeriodFilter, PERIOD_PARAM, PERIOD_TYPE_PARAM } from '../../calendar/ActivityPeriodFilter'
import { ActivityStatusFilter, STATUS_PARAM } from '../../calendar/ActivityStatusFilter'
import { filterActivitiesByStatus, getActivitiesInPeriod, getPeriodDateRange, isActivityPeriodType, isValidPeriodValue, parseActivityStatusParam } from '../../../helpers/calendarHelper'
import { ModalActivityView } from '../../calendar/ModalActivityView'

const useStyles = makeStyles({
   phoneBodyWrapper: {
      display: 'flex',
   },
   textColumn: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: tokens.spacingHorizontalS,
   },
   overFlowHidden: {
      overflow: 'hidden',
   },
   filters: {
      display: 'flex',
      gap: tokens.spacingHorizontalL,
      marginTop: tokens.spacingVerticalM,
      '@media screen and (max-width: 1007px)': {
         gap: tokens.spacingHorizontalS,
      },
      '@media screen and (max-width: 640px)': {
         flexDirection: 'column',
         width: '100%',
      },
   },
})

type ActivitiesCardProps = {
   setActivityModalState: React.Dispatch<
      React.SetStateAction<{
         isOpen: boolean
      }>
   >
   latestActivities: Activity[]
}

export const ActivitiesCard = ({ setActivityModalState, latestActivities }: ActivitiesCardProps) => {
   const classes = useStyles()
   const [searchParams] = useSearchParams()
   const [selectedActivity, setSelectedActivity] = useState<Activity | undefined>(undefined)

   // The overview has no year picker, so periods are resolved against the current year. The label
   // returned by getPeriodDateRange spells the year out so the selection is never ambiguous.
   const year = dayjs().year()
   const periodTypeParam = searchParams.get(PERIOD_TYPE_PARAM)
   const periodParam = searchParams.get(PERIOD_PARAM)
   const periodType = periodTypeParam && isActivityPeriodType(periodTypeParam) ? periodTypeParam : null
   const isFiltered = Boolean(periodType && isValidPeriodValue(periodType, periodParam, year))
   const statusParam = searchParams.get(STATUS_PARAM)
   const status = parseActivityStatusParam(statusParam)
   // No parameter is the default view, which still gets the capped "latest" treatment below.
   const isStatusFiltered = statusParam !== null

   const periodRange = isFiltered ? getPeriodDateRange(periodType, periodParam, year) : null

   const activities = filterActivitiesByStatus(latestActivities, status)

   // Filtering deliberately ignores the active/finished split getSortedLatestActivities applies (and its
   // cap of 12), so every activity matching the filter shows up - a capped list would hide matches.
   const items = isFiltered
      ? getActivitiesInPeriod(activities, periodRange.start, periodRange.end).sort((a, b) => compareByDate(a.startDate, b.startDate))
      : isStatusFiltered
        ? [...activities].sort((a, b) => compareByDate(a.startDate, b.startDate))
        : getSortedLatestActivities(activities)

   if (latestActivities?.length === 0) {
      return (
         <DashboardInfoCard
            title={t('PlanYourCalendar')}
            description={t('PlanYourCalendarDescription')}
            buttonText={t('CreateActivity')}
            onClick={() => setActivityModalState({ isOpen: true })}
         />
      )
   }

   return (
      <>
         <Text size={600} weight="semibold">
            {periodRange ? `${t('AnnualCalendar')} – ${periodRange.label}` : t('AnnualCalendar')}
         </Text>
         <div className={classes.filters}>
            <ActivityPeriodFilter year={year} />
            <ActivityStatusFilter />
         </div>
         {items.length === 0 ? (
            <MessageBar>
               <MessageBarBody>
                  <MessageBarTitle>{t(isFiltered ? 'NoActivitiesInPeriod' : 'NoActivitiesWithStatus')}</MessageBarTitle>
               </MessageBarBody>
            </MessageBar>
         ) : (
            <List
               beigeHeaderBackground
               className={classes.overFlowHidden}
               columns={getDashboardActiviyColumns()}
               items={items}
               onRowClick={setSelectedActivity}
               sortable
               renderPhoneBodyItem={(item) => (
                  <div className={classes.phoneBodyWrapper}>
                     <ActivityStatusBadge item={item} />
                     <div className={classes.textColumn}>
                        <Text size={400}>{formatDate(item.startDate)}</Text>
                        <Text size={400}>{item.title}</Text>
                     </div>
                  </div>
               )}
               columnSizingOptions={{
                  status: { idealWidth: 10, minWidth: 5 },
                  title: { idealWidth: 500, minWidth: 150 },
                  startDate: { idealWidth: 250, minWidth: 100 },
                  endDate: { idealWidth: 250, minWidth: 100 },
               }}
               resizableColumns
            />
         )}
         <ModalActivityView activity={selectedActivity} onClose={() => setSelectedActivity(undefined)} />
      </>
   )
}
